import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AudioLines, ChevronDown, Copy, Feather, ImageDown, Music, Video } from "lucide-react";
import { toast } from "sonner";
import SectionHeading from "./SectionHeading";
import Reveal from "./Reveal";
import Modal from "./Modal";
import { bookColors, books, poems, songs } from "../../data/content";
import { sharePoemImage } from "../../lib/sharePoem";
import { goToVideoPoem } from "../../lib/scroll";

const audioBooks = new Set(books.filter((b) => b.spotify).map((b) => b.id));

// Dos poemas destacados por libro (prioriza los que tienen audio exacto en Spotify)
const featuredIds = [
    "te-me", "recordar",
    "verte-otra-vez", "tremulo",
    "desprenderse", "casi",
    "tu-sabor", "nuna",
];
const featuredPoems = featuredIds.map((id) => poems.find((p) => p.id === id)).filter(Boolean);

const Poems = () => {
    const [active, setActive] = useState(null);
    const [sharing, setSharing] = useState(false);
    const [poemsExpanded, setPoemsExpanded] = useState(false);
    const firstTwoPoems = featuredPoems.slice(0, 2);
    const restPoems = featuredPoems.slice(2);

    const shareImage = async (poem) => {
        setSharing(true);
        try {
            const result = await sharePoemImage(poem);
            if (result === "shared") {
                toast.success("Imagen lista: elige «Guardar imagen» para tenerla en tu galería");
            } else {
                toast.success("Imagen descargada: búscala en tu carpeta de Descargas");
            }
        } catch (err) {
            if (err?.name !== "AbortError") toast.error("No se pudo generar la imagen");
        } finally {
            setSharing(false);
        }
    };

    const listenSong = (poem) => {
        const song = songs.find((s) => s.poemId === poem.id);
        if (!song) return;
        setActive(null);
        window.__lenis?.scrollTo("#canciones", { offset: -70 });
        window.dispatchEvent(new CustomEvent("play-song", { detail: song.id }));
    };

    const PoemCard = ({ poem, i }) => (
        <Reveal delay={Math.min(i, 6) * 0.07}>
            <article
                data-testid={`poem-card-${i}`}
                className="group h-full border border-line bg-paper p-7 flex flex-col hover:border-ink hover:shadow-lg transition-[border-color,box-shadow] duration-400"
                style={{ borderTop: `3px solid ${bookColors[poem.bookId]}` }}
            >
                <p
                    className="font-mono text-[10px] uppercase tracking-[0.22em]"
                    style={{ color: bookColors[poem.bookId] }}
                >
                    {poem.book} · {poem.year}
                </p>
                {audioBooks.has(poem.bookId) && (
                    <p className="mt-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-golddeep">
                        <AudioLines size={11} />
                        Con audio
                    </p>
                )}
                <h3 className="mt-3 font-display text-2xl text-ink">{poem.title}</h3>
                <p className="mt-4 font-display italic text-lg leading-relaxed text-ink/70 flex-1">
                    {poem.lines.filter(Boolean).slice(0, 3).join(" / ")}…
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3">
                    <button
                        data-testid={`poem-reader-open-${i}`}
                        onClick={() => setActive(poem)}
                        className="self-start flex items-center gap-2 border-b border-ink pb-1 font-mono text-[11px] uppercase tracking-[0.22em] text-ink hover:text-crimson hover:border-crimson transition-colors"
                    >
                        <Feather size={13} />
                        Leer poema
                    </button>
                    {songs.some((s) => s.poemId === poem.id) && (
                        <button
                            data-testid={`poem-card-song-${i}`}
                            onClick={() => listenSong(poem)}
                            className="flex items-center gap-2 border-b border-golddeep pb-1 font-mono text-[11px] uppercase tracking-[0.22em] text-golddeep hover:text-ink hover:border-ink transition-colors"
                        >
                            <Music size={12} />
                            Escuchar canción
                        </button>
                    )}
                    {poem.videoId && (
                        <button
                            data-testid={`poem-card-video-${i}`}
                            onClick={() => goToVideoPoem(poem.videoId)}
                            className="flex items-center gap-2 border-b border-crimson pb-1 font-mono text-[11px] uppercase tracking-[0.22em] text-crimson hover:text-ink hover:border-ink transition-colors"
                        >
                            <Video size={12} />
                            Ver vídeo
                        </button>
                    )}
                </div>
            </article>
        </Reveal>
    );

    const copyPoem = (poem) => {
        const text = `${poem.title}\nJosé Luis Garcés Maldonado — ${poem.book} (${poem.year})\n\n${poem.lines.join("\n")}`;
        navigator.clipboard.writeText(text).then(
            () => toast.success("Poema copiado al portapapeles"),
            () => toast.error("No se pudo copiar el poema")
        );
    };

    return (
        <section id="poemas" data-testid="poems-section" className="relative py-24 sm:py-36 overflow-hidden">
            <img
                src="/media/trazo-flor.png"
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute -right-10 top-6 w-72 sm:w-96 opacity-[0.09] rotate-[8deg]"
            />
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <SectionHeading
                    kicker="03 — Antología"
                    title="Poemas destacados"
                    description="Una selección de versos de los cuatro poemarios, extraídos directamente de las ediciones impresas."
                />

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                    {firstTwoPoems.map((poem, i) => (
                        <PoemCard key={poem.id} poem={poem} i={i} />
                    ))}
                </div>

                <div className="mt-10 flex justify-center">
                    <button
                        data-testid="poem-more-toggle"
                        onClick={() => setPoemsExpanded(!poemsExpanded)}
                        aria-expanded={poemsExpanded}
                        className="flex items-center gap-3 border border-ink px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] text-ink hover:bg-ink hover:text-paper transition-colors duration-300"
                    >
                        {poemsExpanded ? "Leer menos" : "Leer más"}
                        <ChevronDown
                            size={14}
                            className={`transition-transform duration-300 ${poemsExpanded ? "rotate-180" : ""}`}
                        />
                    </button>
                </div>

                <AnimatePresence initial={false}>
                    {poemsExpanded && (
                        <motion.div
                            data-testid="poem-more-grid"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                            className="overflow-hidden"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 pt-10">
                                {restPoems.map((poem, i) => (
                                    <PoemCard key={poem.id} poem={poem} i={i + 2} />
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            <Modal
                open={!!active}
                onClose={() => setActive(null)}
                label={active ? `Poema ${active.title}` : ""}
                testId="poem-reader-modal"
            >
                {active && (
                    <div className="p-8 sm:p-14">
                        <p
                            className="font-mono text-[10px] uppercase tracking-[0.28em]"
                            style={{ color: bookColors[active.bookId] }}
                        >
                            {active.book} · {active.year}
                        </p>
                        <h3 className="mt-4 font-display text-4xl sm:text-5xl font-light text-ink">
                            {active.title}
                        </h3>
                        <div className="mt-10 max-w-md">
                            {active.lines.map((line, i) =>
                                line === "" ? (
                                    <div key={i} className="h-6" />
                                ) : (
                                    <motion.p
                                        key={i}
                                        className="font-display text-xl sm:text-2xl leading-[1.5] text-ink/90"
                                        initial={{ opacity: 0, y: 12 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.15 + i * 0.05, duration: 0.5 }}
                                    >
                                        {line}
                                    </motion.p>
                                )
                            )}
                        </div>
                        {audioBooks.has(active.bookId) && (
                            <div className="mt-10 border-t border-line pt-8">
                                <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-crimson">
                                    <AudioLines size={13} />
                                    Escucha este poema en Spotify
                                </p>
                                <div
                                    data-testid="poem-spotify-embed"
                                    className="mt-5 overflow-hidden rounded-xl border border-line"
                                >
                                    <iframe
                                        src={
                                            active.spotifyTrack
                                                ? `https://open.spotify.com/embed/track/${active.spotifyTrack}`
                                                : "https://open.spotify.com/embed/artist/1dve92cCVC0kUxbwgVhQa2"
                                        }
                                        title={`Spotify: ${active.title}`}
                                        width="100%"
                                        height={active.spotifyTrack ? 152 : 352}
                                        style={{ border: 0, display: "block" }}
                                        loading="lazy"
                                        allowFullScreen
                                        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                                    />
                                </div>
                                {!active.spotifyTrack && (
                                    <p className="mt-3 font-mono text-[10px] tracking-[0.12em] text-subtle">
                                        Busca «{active.title}» en el reproductor o escanea el código Spotify de la edición impresa
                                    </p>
                                )}
                            </div>
                        )}
                        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-line pt-6">
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">
                                José Luis Garcés Maldonado
                            </p>
                            <div className="flex flex-wrap items-center gap-3">
                                {songs.some((s) => s.poemId === active.id) && (
                                    <button
                                        data-testid="poem-song-link"
                                        onClick={() => listenSong(active)}
                                        className="flex items-center gap-2 border border-golddeep px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-golddeep hover:bg-golddeep hover:text-paper transition-colors"
                                    >
                                        <Music size={12} />
                                        Escuchar la canción
                                    </button>
                                )}
                                {active.videoId && (
                                    <button
                                        data-testid="poem-video-link"
                                        onClick={() => {
                                            setActive(null);
                                            goToVideoPoem(active.videoId);
                                        }}
                                        className="flex items-center gap-2 border border-crimson px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-crimson hover:bg-crimson hover:text-paper transition-colors"
                                    >
                                        <Video size={12} />
                                        Ver vídeo poema
                                    </button>
                                )}
                                <button
                                    data-testid="poem-share-image-button"
                                    onClick={() => shareImage(active)}
                                    disabled={sharing}
                                    className="flex items-center gap-2 border border-crimson px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-crimson hover:bg-crimson hover:text-paper transition-colors disabled:opacity-50"
                                >
                                    <ImageDown size={12} />
                                    {sharing ? "Generando…" : "Compartir"}
                                </button>
                                <button
                                    data-testid="poem-copy-button"
                                    onClick={() => copyPoem(active)}
                                    className="flex items-center gap-2 border border-ink px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink hover:bg-ink hover:text-paper transition-colors"
                                >
                                    <Copy size={12} />
                                    Copiar poema
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </section>
    );
};

export default Poems;
